// @flow
import React from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import OverviewCreatorPromotablePinsGrid from 'app/common/react/components/growth/smb/OverviewCreatorPromotablePinsGrid/OverviewCreatorPromotablePinsGrid';
import SectionHeader from 'app/common/react/components/profile/SectionHeader/SectionHeader';
import stopProfileTiming from 'app/common/react/components/profile/ProfileBoards/utils/stopProfileTiming';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withResource from 'app/common/lib/react/withResource';
import withViewer from 'app/common/lib/react/withViewer';
import { userInQuickPromoteCountry } from 'app/common/lib/BusinessUtil';
import { UserProfilePinGrid } from 'app/common/react/components/profile/UserProfilePinGrid/UserProfilePinGrid';
import { withRouter } from 'react-router';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Container } from 'gestalt';
import { compose, type Dispatch } from 'redux';

type OwnProps = {|
  isOwnProfile: boolean,
  isPartner: boolean,
  pins: {
    data: Array<Object>,
    fetchMore: () => void,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  requestContext: RequestContextType,
  user: ProfileUser,
  userId?: number,
  username: string,
  viewer: Viewer,
|};

export type Props = {|
  ...OwnProps,
  dispatch?: Dispatch<any>,
|};

class ProfileActivity extends React.PureComponent<Props> {
  componentDidUpdate(prevProps: Props) {
    const { isOwnProfile, pins, user } = this.props;
    const { show_creator_profile: showCreatorProfile } = user;

    // Stopping timer for PWT reporting on profiles for first resource load
    if (pins.isLoaded && !prevProps.pins.isLoaded) {
      stopProfileTiming({ isOwnProfile, showCreatorProfile });
    }
  }

  render() {
    const { isOwnProfile, isPartner, pins, viewer, requestContext, user } = this.props;
    const { id, username } = user;

    if (!user || !pins || !pins.data) {
      return null;
    }

    const timeSpentViewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;

    const showPromotablePinSection =
      isOwnProfile && isPartner && userInQuickPromoteCountry(viewer, requestContext);

    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={timeSpentViewParam}
        component={PCONST.thrift.ComponentType.USER_OVERVIEW}
      >
        <Box>
          {showPromotablePinSection && (
            <Box marginBottom={8}>
              <OverviewCreatorPromotablePinsGrid id={id} username={username} />
            </Box>
          )}
          <Box>
            <Container>
              <Box
                dangerouslySetInlineStyle={{
                  __style: {
                    marginLeft: '10px',
                  },
                }}
              >
                <SectionHeader label={i18n._('Latest', 'label for latest pins on activity page')} />
              </Box>
              <Box justifyContent="center" display="flex">
                <Box
                  maxWidth={820}
                  width="100%"
                  dangerouslySetInlineStyle={{
                    __style: {
                      marginLeft: '20px',
                    },
                  }}
                >
                  <UserProfilePinGrid
                    isOverview
                    isOwnProfile={isOwnProfile}
                    user={user}
                    username={user.username}
                    pins={pins}
                    viewer={viewer}
                  />
                </Box>
              </Box>
            </Container>
          </Box>
        </Box>
      </TimeSpent>
    );
  }
}

const mapStateToProps = (state): {| isPartner: boolean |} => ({
  isPartner: state.viewer.isAuth && state.viewer.isPartner,
});

export default compose(
  connect<Props, OwnProps, _, _, _, _, _>(mapStateToProps),
  withViewer,
  withResource({
    name: 'UserActivityPinsResource',
    key: 'pins',
    options: ({ username, userId, viewer, isOwnProfile, user }) => ({
      exclude_add_pin_rep: !isOwnProfile,
      is_own_profile_pins: isOwnProfile,
      user_id: userId || user.id,
      username,
      field_set_key: viewer.isPartner && isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
  withRequestContext,
  withRouter,
)(ProfileActivity);
